'use client';

import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Send, MoonStar, Globe2, Plane, Shield } from 'lucide-react';
import type { Advisory } from '../../services/advisoryRules';
import { ChevronDownIcon } from '../ui/IconSet';
import SectionAdvisories from './SectionAdvisories';

type QuoteSummaryProps = {
  totals: {
    base: number;
    landing: number;
    handling: number;
    nav: number;
    taxes: number;
    aircraftTax: number;
    safetySecurity: number;
    totalPax?: number;
      perPaxDepartureTax?: number;
      perPaxSafetySecurity?: number;
      regulatoryByLeg?: Array<{
        id: string;
        from: string;
        to: string;
        pax: number;
        departureTaxTotal: number;
        safetySecurityTotal: number;
      }>;
      legBreakdowns?: Array<{
        id: string;
        from: string;
        to: string;
        isDeadLeg?: boolean;
        isImmigrationLeg?: boolean;
        pax: number;
        distanceMiles: number;
        blockMinutes: number;
        baseCharge: number;
        landing: number;
        handling: number;
        navigation: number;
        departureTaxes: number;
        aircraftTax: number;
        aircraftTaxComponents: {
          landing: number;
          handling: number;
          navigation: number;
          paxBusLuggage: number;
          aircraftPermit: number;
          clearance: number;
          reposition: number;
        };
        safetySecurity: number;
        perPaxDepartureTax: number;
        perPaxSafetySecurity: number;
        ancillaries: number;
        extraServices: number;
        waitingTimeCharge: number;
        sleepOverCharge: number;
        total: number;
        isInternational: boolean;
      }>;
    maFees: number;
    loungeFees: number;
    ancillaryLines: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      name: string;
      scope: 'ORIGIN' | 'DESTINATION';
      unitPrice?: number;
      paxCount?: number;
      amount: number;
    }>;
    extraServices: number;
    extraServiceLines?: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      code: string;
      name: string;
      scope: string;
      pricingModel?: 'FIXED' | 'PER_PAX' | 'PER_HOUR';
      unitPrice?: number;
      quantity?: number;
      paxCount?: number;
      blockMinutes?: number;
      amount: number;
    }>;
    waiting: number;
    discountVal: number;
    discountBreakdown?: {
      clientPercent: number;
      issuerPercent: number;
      ceoPercent: number;
      totalPercent: number;
      clientAmount: number;
      issuerAmount: number;
      ceoAmount: number;
    };
    total: number;
    margin: number;
  };
  issuerDiscount: number;
  ceoDiscount: number;
  issuerDiscountLimit: number;
  onIssuerDiscountChange: (value: number) => void;
  onCeoDiscountChange: (value: number) => void;
  canEditCeoDiscount: boolean;
  onSave: () => void;
  canAuthorizeQuote?: boolean;
  authorizeBlockReason?: string;
  includeBreakdownDefault: boolean;
  onToggleIncludeBreakdown: (value: boolean) => void;
  advisories?: Advisory[];
  guidanceNotes?: string;
};

function CostLine({
  label,
  value,
  helper,
}: {
  label: string;
  value: number;
  helper?: string;
}) {
  const helperLines = helper?.split(' | ').map(line => line.trim()).filter(Boolean) || [];
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11px] font-semibold text-slate-200 uppercase tracking-[0.14em]">{label}</span>
        {helperLines.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {helperLines.map((line, idx) => (
              <span key={`${label}-${idx}`} className="block text-[10px] leading-4 text-slate-300/85">
                {line}
              </span>
            ))}
          </div>
        )}
      </div>
      <span className="whitespace-nowrap text-sm font-mono font-semibold text-slate-100">
        ${Math.round(value).toLocaleString()}
      </span>
    </div>
  );
}

function GroupHeader({ label }: { label: string }) {
  return (
    <div className="pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">
      {label}
    </div>
  );
}

const QuoteSummary: React.FC<QuoteSummaryProps> = ({
  totals,
  issuerDiscount,
  ceoDiscount,
  issuerDiscountLimit,
  onIssuerDiscountChange,
  onCeoDiscountChange,
  canEditCeoDiscount,
  onSave,
  canAuthorizeQuote = true,
  authorizeBlockReason = '',
  includeBreakdownDefault,
  onToggleIncludeBreakdown,
  advisories = [],
  guidanceNotes = '',
}) => {
  const [openLegIds, setOpenLegIds] = useState<string[]>([]);
  const marginPercent = totals.total > 0 ? (totals.margin / totals.total) * 100 : 0;
  const isOverLimit = issuerDiscount > issuerDiscountLimit;
  const isHealthyMargin = marginPercent > 15;

  useEffect(() => {
    if (!totals.legBreakdowns?.length) {
      setOpenLegIds([]);
      return;
    }
    const validIds = new Set(totals.legBreakdowns.map(item => item.id));
    const lastId = totals.legBreakdowns[totals.legBreakdowns.length - 1]?.id;
    setOpenLegIds(prev => {
      const kept = prev.filter(id => validIds.has(id));
      if (!lastId || kept.includes(lastId)) return kept;
      return [...kept, lastId];
    });
  }, [totals.legBreakdowns]);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-blue-950 rounded-3xl shadow-2xl h-fit sticky top-4 overflow-hidden border border-slate-700/50 backdrop-blur-sm">
      {/* Header */}
      <div className="px-7 py-5 border-b border-slate-700/40 bg-gradient-to-r from-slate-900/80 to-slate-800/50">
        <p className="text-[11px] font-bold text-cyan-300 uppercase tracking-[0.25em]">
          Yield & Risk Synthesis
        </p>
      </div>

      {/* Cost Breakdown */}
      <div className="px-6 py-5 space-y-1 border-b border-slate-700/60">
        {totals.legBreakdowns?.length ? (
          <div className="space-y-2 pb-1">
            <GroupHeader label="Leg Breakdown" />
            {totals.legBreakdowns.map((item, index) => {
              const isOpen = openLegIds.includes(item.id);
              const isPositioningLeg = Boolean(item.isDeadLeg) || Number(item.pax || 0) === 0;
              const isImmigrationLeg = Boolean(item.isImmigrationLeg);
              const hasSleepOverCharge = Number(item.sleepOverCharge || 0) > 0;
              return (
                <div key={item.id} className={`rounded-xl border bg-slate-800/80 backdrop-blur ${
                  item.isInternational 
                    ? 'border-amber-400/70 bg-gradient-to-br from-slate-800/90 via-amber-900/15 to-slate-800/90' 
                    : 'border-slate-600/80'
                }`}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenLegIds(prev =>
                        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                      )
                    }
                    className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left"
                  >
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-1 pr-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-300/90">
                          Leg {String(index + 1).padStart(2, '0')}
                        </div>
                        {item.isInternational && (
                          <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-amber-400 bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                            <Globe2 className="h-2.5 w-2.5" />
                            Intl
                          </span>
                        )}
                        {isPositioningLeg && (
                          <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-sky-300/70 bg-gradient-to-r from-sky-600 to-cyan-500 px-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                            <Plane className="h-2.5 w-2.5" />
                            Pos
                          </span>
                        )}
                        {isImmigrationLeg && (
                          <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-emerald-300/70 bg-gradient-to-r from-emerald-600 to-teal-500 px-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                            <Shield className="h-2.5 w-2.5" />
                            Im
                          </span>
                        )}
                        {hasSleepOverCharge && (
                          <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-violet-300/70 bg-gradient-to-r from-violet-600 to-indigo-600 px-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                            <MoonStar className="h-2.5 w-2.5" />
                            Ngt
                          </span>
                        )}
                      </div>
                      <div className="text-[15px] font-semibold leading-5 text-slate-100">
                        {item.from} → {item.to}
                      </div>
                      <div className="text-[11px] text-slate-300/85">
                        {item.pax} pax · {Math.round(item.distanceMiles)} mi · {Math.round(item.blockMinutes)} min
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-base font-mono font-semibold text-slate-100">
                        ${Math.round(item.total).toLocaleString()}
                      </div>
                      <span className="rounded-full border border-slate-500 bg-slate-800 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-100 chevron-btn">
                        <ChevronDownIcon className={`w-3 h-3 ${isOpen ? 'chevron-rotated' : ''}`} />
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-3.5 pb-3.5 pt-2 space-y-0.5 border-t border-slate-700/50">
                      <CostLine label="Base Charge" value={item.baseCharge} />
                      <CostLine
                        label="Aircraft Taxes"
                        value={item.aircraftTax}
                        helper={`Landing ${Math.round(item.aircraftTaxComponents.landing)} + Handling ${Math.round(item.aircraftTaxComponents.handling)} + Nav ${Math.round(item.aircraftTaxComponents.navigation)} + PaxBus ${Math.round(item.aircraftTaxComponents.paxBusLuggage)} + Permit ${Math.round(item.aircraftTaxComponents.aircraftPermit)} + Clearance ${Math.round(item.aircraftTaxComponents.clearance)} + Reposition ${Math.round(item.aircraftTaxComponents.reposition)}`}
                      />
                      <CostLine
                        label="Departure Taxes"
                        value={item.departureTaxes}
                        helper={`$${item.perPaxDepartureTax.toFixed(2)} / pax · ${item.pax} pax`}
                      />
                      <CostLine
                        label="Safety & Security"
                        value={item.safetySecurity}
                        helper={`$${item.perPaxSafetySecurity.toFixed(2)} / pax · ${item.pax} pax`}
                      />
                      <CostLine
                        label="Ancillary Services"
                        value={item.ancillaries}
                        helper={
                          (() => {
                            const lines = (totals.ancillaryLines || []).filter(
                              line => line.legId === item.id && line.amount > 0
                            );
                            if (!lines.length) return 'No ancillary charges on this leg';
                            return lines
                              .map(
                                line =>
                                  `${line.name} (${line.scope === 'ORIGIN' ? 'Departure' : 'Destination'}): $${(line.unitPrice || 0).toFixed(2)} / pax · ${line.paxCount || 0} pax`
                              )
                              .join(' | ');
                          })()
                        }
                      />
                      <CostLine
                        label="Extra Services"
                        value={item.extraServices}
                        helper={
                          (() => {
                            const lines = (totals.extraServiceLines || []).filter(
                              line => line.legId === item.id && line.amount > 0
                            );
                            if (!lines.length) return 'No extra-service charges on this leg';
                            return lines
                              .map(line => {
                                const scopeLabel =
                                  line.scope === 'ORIGIN'
                                    ? 'Departure'
                                    : line.scope === 'DESTINATION'
                                      ? 'Destination'
                                      : line.scope;
                                if (line.pricingModel === 'PER_PAX') {
                                  return `${line.name} (${scopeLabel}): $${(line.unitPrice || 0).toFixed(2)} / pax · ${line.paxCount || 0} pax`;
                                }
                                if (line.pricingModel === 'PER_HOUR') {
                                  const hours = ((line.blockMinutes || 0) / 60).toFixed(2);
                                  return `${line.name} (${scopeLabel}): $${(line.unitPrice || 0).toFixed(2)} / hr · ${hours} hr`;
                                }
                                return `${line.name} (${scopeLabel}): $${(line.unitPrice || 0).toFixed(2)} fixed · qty ${line.quantity || 1}`;
                              })
                              .join(' | ');
                          })()
                        }
                      />
                      <CostLine
                        label="Waiting Time"
                        value={item.waitingTimeCharge || 0}
                        helper={item.waitingTimeCharge > 0 ? 'Applied on operational anchor leg' : 'No waiting-time charge on this leg'}
                      />
                      <CostLine
                        label="Sleep Over"
                        value={item.sleepOverCharge || 0}
                        helper={item.sleepOverCharge > 0 ? 'Applied on operational anchor leg' : 'No sleep-over charge on this leg'}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

      </div>

      <div className="px-7 py-5 border-b border-slate-700/60">
        <div className="space-y-3">
          <SectionAdvisories title="Final Audit" section="FINAL" advisories={advisories} notes={guidanceNotes} compact />
          <SectionAdvisories title="Discount Guard" section="DISCOUNT" advisories={advisories} compact />
        </div>
      </div>

      <div className="px-7 py-5 border-b border-slate-700/60">
        <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-600 bg-slate-800/70 px-4 py-3 text-sm text-slate-100">
          <span className="text-xs uppercase tracking-[0.1em] text-slate-200">Send Breakdown by Default</span>
          <input
            type="checkbox"
            checked={includeBreakdownDefault}
            onChange={e => onToggleIncludeBreakdown(e.target.checked)}
            className="h-4 w-4 accent-teal-400"
          />
        </label>
        <p className="mt-2 text-[11px] text-slate-300/80">
          This pre-fills the send dialog. You can override per email.
        </p>
      </div>

      {/* Discount Section */}
      <div className="px-7 py-6 space-y-4 border-b border-slate-700/60">
        <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-200 uppercase tracking-[0.12em]">
              Issuer Discount %
            </span>
          <span className={`text-[10px] font-semibold ${
            isOverLimit ? 'text-red-400' : 'text-teal-400'
          }`}>
            LIMIT: {issuerDiscountLimit}%
          </span>
        </div>
        
        {/* Slider */}
        <div className="space-y-3">
          <input
            type="range"
            min="0"
            max={issuerDiscountLimit}
            step="0.5"
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-slate-700 accent-cyan-400"
            value={issuerDiscount}
            onChange={e => onIssuerDiscountChange(parseFloat(e.target.value))}
          />
          <input
            type="number"
            min="0"
            max={issuerDiscountLimit}
            step="0.1"
            value={issuerDiscount}
            onChange={e => onIssuerDiscountChange(parseFloat(e.target.value) || 0)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-sm font-mono font-semibold text-slate-100 text-center outline-none transition-all focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/50"
          />
        </div>

        {canEditCeoDiscount && (
          <div className="space-y-3 rounded-lg border border-indigo-400/30 bg-indigo-500/10 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-indigo-200 uppercase tracking-[0.12em]">Chief Executive Discount %</span>
              <span className="text-[10px] font-semibold text-indigo-300">Private</span>
            </div>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={ceoDiscount}
              onChange={e => onCeoDiscountChange(parseFloat(e.target.value) || 0)}
              className="w-full bg-slate-800 border border-indigo-500/40 rounded-lg px-4 py-3 text-sm font-mono font-semibold text-slate-100 text-center outline-none transition-all focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400/50"
            />
          </div>
        )}

        {!!totals.discountBreakdown && (
          <div className="space-y-1 text-xs text-slate-300">
            <div className="flex items-center justify-between">
              <span>Client Terms Discount</span>
              <span className="font-mono">-{totals.discountBreakdown.clientPercent.toFixed(1)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Issuer Discount</span>
              <span className="font-mono">-{totals.discountBreakdown.issuerPercent.toFixed(1)}%</span>
            </div>
            {canEditCeoDiscount && (
              <div className="flex items-center justify-between">
                <span>Chief Executive Discount</span>
                <span className="font-mono">-{totals.discountBreakdown.ceoPercent.toFixed(1)}%</span>
              </div>
            )}
          </div>
        )}

        {totals.discountVal > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">Adjustment:</span>
            <span className="text-sm font-mono font-semibold text-red-400">
              -${Math.round(totals.discountVal).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="px-7 py-6 space-y-4 border-b border-slate-700/60">
        {/* Contract Value */}
        <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-200 uppercase tracking-[0.12em]">
              Contract Value
            </span>
          <p className="text-2xl font-bold font-mono text-teal-400 tracking-tight">
            ${Math.round(totals.total).toLocaleString()}
          </p>
        </div>

        {/* Margin */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-slate-200 uppercase tracking-[0.12em]">
                Est. Net Margin
              </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-mono font-semibold ${
              totals.margin > 0 ? 'text-teal-400' : 'text-red-400'
            }`}>
              ${Math.round(totals.margin).toLocaleString()}
            </span>
            <span className={`text-xs font-medium ${
              isHealthyMargin ? 'text-teal-400' : 'text-amber-400'
            }`}>
              ({marginPercent.toFixed(1)}%)
            </span>
            {isHealthyMargin ? (
              <TrendingUp className="w-3.5 h-3.5 text-teal-400" />
            ) : (
              <TrendingDown className="w-3.5 h-3.5 text-amber-400" />
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="p-6">
        {!canAuthorizeQuote && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-400/40 mb-4">
            <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <p className="text-xs text-rose-300">
              {authorizeBlockReason || 'Quote cannot be authorized yet.'}
            </p>
          </div>
        )}
        {isOverLimit && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-400/40 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              Discount exceeds your limit. This quote will require DFO approval.
            </p>
          </div>
        )}
        
        <button
          type="button"
          onClick={onSave}
          disabled={!canAuthorizeQuote}
          className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all btn-glow ${
            !canAuthorizeQuote
              ? 'bg-slate-700/40 text-slate-400 border border-slate-600/50 cursor-not-allowed'
              : isOverLimit
              ? 'btn-action bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30'
              : 'btn-premium'
          }`}
        >
          {isOverLimit ? (
            <>
              <Send className="w-4 h-4" />
              Submit for Approval
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Authorize Quote
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default QuoteSummary;
